<template>
  <q-card
    class="video-container-card"
    :class="{
      'video-container-card-desktop fixed-top-right col-lg-4 col-md-4 col-xs-12 offset-md-1 offset-xs-0': !isMaximized,
      'video-container-card-maximized absolute-top col-12 offset-0': isMaximized,
    }"
    :style="videoContainerStyle"
  >
    <video-player-container class="video-player-container">
      <div
        v-if="!camera"
        class="no-local-video"
      >
        <span class="material-icons">videocam_off</span>
      </div>
      <div
        v-if="isRemoteParticipantOffCamera"
        class="no-remote-video"
      >
        <span class="material-icons">person</span>
      </div>
      <div
        v-if="!isRemoteParticipantConnected"
        class="wait-participant-text"
      >
        <span>{{ waitingForUserText }}</span>
      </div>
    </video-player-container>
    <div
      v-if="isShowVideoCallPanel"
      class="menu-panel"
    >
      <q-btn
        flat
        color="primary"
        size="sm"
        :icon="camera ? 'videocam' : 'videocam_off'"
        :disable="!isActiveStream"
        @click="toggleZoomVideo"
      />
      <q-btn
        flat
        color="primary"
        size="sm"
        :icon="isAudioOn ? 'mic' : 'mic_off'"
        :disable="!isActiveStream"
        @click="toggleZoomAudio"
      />
      <q-btn
        type="submit"
        flat
        size="sm"
        icon="call_end"
        color="negative"
        :disable="!isActiveStream"
        @click="leaveCall"
      />
      <q-btn
        flat
        size="sm"
        icon="settings"
        color="primary"
        :disable="!isActiveStream"
        @click="openSettings"
      />
      <q-btn
        v-if="isVideoSessionCreator"
        color="primary"
        flat
        icon="more_horiz"
        :disable="!isActiveStream || !isRemoteParticipantConnected"
      >
        <q-menu>
          <q-list style="min-width: 100px">
            <q-item
              v-ripple
              v-close-popup
              clickable
              @click="toggleZoomRemoteAudio"
            >
              <q-item-section avatar>
                <q-icon
                  :name="isRemoteParticipantOffAudio ? 'mic_off' : 'mic'"
                />
              </q-item-section>
              <q-item-section class="text-no-wrap">
                {{ isRemoteParticipantOffAudio
                  ? $t('sessions.videoSession.controlVideoChatMenu.unmuteAudio')
                  : $t('sessions.videoSession.controlVideoChatMenu.muteAudio')
                }}
              </q-item-section>
            </q-item>
            <q-item
              v-ripple
              v-close-popup
              clickable
              @click="controlRemoteParticipantVideoCall(isRemoteParticipantOffCamera ? 'video_on' : 'video_off')"
            >
              <q-item-section avatar>
                <q-icon :name="isRemoteParticipantOffCamera ? 'videocam_off' : 'videocam'" />
              </q-item-section>
              <q-item-section>
                {{
                  isRemoteParticipantOffCamera
                    ? $t('sessions.videoSession.controlVideoChatMenu.turnOnVideo')
                    : $t('sessions.videoSession.controlVideoChatMenu.turnOffVideo')
                }}
              </q-item-section>
            </q-item>
            <q-item
              v-ripple
              v-close-popup
              clickable
              @click="controlRemoteParticipantVideoCall('disconnect')"
            >
              <q-item-section avatar>
                <q-icon name="call_end" />
              </q-item-section>
              <q-item-section>
                {{ $t('sessions.videoSession.controlVideoChatMenu.disconnect') }}
              </q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </q-btn>
      <q-space />
      <q-btn
        flat
        size="sm"
        icon="minimize"
        color="primary"
        @click="onMinimizeVideo"
      >
        <q-tooltip>
          {{ $t('sessions.videoSession.tooltipMinimizeVideo') }}
        </q-tooltip>
      </q-btn>
      <template v-if="!$q.platform.is.mobile">
        <q-btn
          v-if="isMaximized"
          flat
          size="sm"
          icon="close_fullscreen"
          color="primary"
          @click="onRestoreMaximizedVideo"
        >
          <q-tooltip>
            {{ $t('sessions.videoSession.tooltipRestoreVideo') }}
          </q-tooltip>
        </q-btn>
        <q-btn
          v-else
          flat
          size="sm"
          icon="open_in_full"
          color="primary"
          @click="onMaximizeVideo"
        >
          <q-tooltip>
            {{ $t('sessions.videoSession.tooltipMaximizeVideo') }}
          </q-tooltip>
        </q-btn>
      </template>
    </div>
  </q-card>
</template>

<script>

import VideoSettings from 'components/VideoChat/Modals/VideoSettings';
import { mapState } from 'pinia';
import { IS_INVITATION_ACCEPTED } from 'src/constants/videoSession';
import Interactions from 'src/mixins/Interactions';
import VideoChat from 'src/mixins/VideoChat';
import { auth } from 'src/store/auth';
import { currentVideoSession } from 'src/store/currentVideoSession';

const SESSION_PROGRESS_CLOSED_HEIGHT_PX = 50;
const VIDEO_CONTAINER_INDENT_PX = 10;

export default {
  name: 'VideoContainer',

  mixins: [Interactions, VideoChat],

  props: {
    activeSession: {
      type: Object,
      default: () => ({}),
    },
    currentClient: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    headerHeightPx: {
      type: Number,
      default: null,
    },
  },

  emits: ['minimize'],

  data() {
    return {
      isMaximized: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),
    ...mapState(currentVideoSession, [
      'microphone',
      'camera',
      'isRemoteParticipantOffCamera',
      'isRemoteParticipantOffAudio',
      'isRemoteParticipantConnected',
    ]),

    isActiveStream() {
      return this.activeZoomStream !== null;
    },

    isAudioOn() {
      if (!this.isInvitedUser) {
        return this.microphone;
      }

      return this.microphone && !this.isRemoteParticipantOffAudio;
    },

    videoContainerStyle() {
      const headerHeight = this.headerHeightPx || 0;

      if (this.isMaximized) {
        return {
          height: `calc(100vh - ${headerHeight + SESSION_PROGRESS_CLOSED_HEIGHT_PX + VIDEO_CONTAINER_INDENT_PX}px)`,
        };
      }

      return {
        top: `${headerHeight + VIDEO_CONTAINER_INDENT_PX}px`,
      };
    },
  },

  methods: {
    onMinimizeVideo() {
      this.$emit('minimize');
    }, // onMinimizeVideo()

    onMaximizeVideo() {
      this.isMaximized = true;
    }, // onMaximizeVideo()

    onRestoreMaximizedVideo() {
      this.isMaximized = false;
    }, // onRestoreMaximizedVideo()

    showVideoCallRoom(invitedUserId) {
      this.showRoom(invitedUserId);
    }, // showVideoCallRoom()

    async leaveCall(method, force) {
      sessionStorage.removeItem(IS_INVITATION_ACCEPTED);
      await this.leaveZoomVideoCall(method, force);
    }, // leaveCall

    openSettings() {
      this.openComponentDialog(
        VideoSettings,
        {
          zoomClient: this.zoomVideoClient,
        },
        ({ camera, microphone, speaker }) => {
          this.updateDevices(camera, microphone, speaker);
        },
      );
    }, // openSettings()
  },
};
</script>

<style lang="scss">
.video-container-card {
  display: flex;
  flex-direction: column;
  z-index: 5000;
}
.video-container-card-desktop {
  height: auto;
}
.video-container-card-maximized {
  border-radius: 0;
}

.video-player-container {
  width: 100%;
  height: calc( 100% - 45px);
  background-color: $dark;
  aspect-ratio: 16 / 9;

  video-player {
    div {
      aspect-ratio: 16 / 9;
    }
  }
}

.local-video {
  position: absolute;
  width: 35%;
  top: 20px;
  right: 5%;
  z-index: 5001;
  border-radius: 7px;

  video {
    display: block;
  }
}

.local-video.active-speaker {
  border: #1494F0 3px solid;
}

.remote-video {
  height: 100%;
  width: 100%;
  object-fit: cover;

  video {
    display: block;
  }
}

.remote-video.active-speaker {
  box-sizing: border-box;
  border: #1494F0 3px solid;
}

.no-local-video {
  position: absolute;
  width: 35%;
  height: auto;
  min-height: 85px;
  top: 20px;
  right: 5%;
  background: $grey-10;
  border-radius: 7px;
  display: flex;
  justify-content: center;
  z-index: 5001;
  span {
    color: white;
    font-size: 24px;
  }
}

.no-remote-video {
  position: absolute;
  background: $dark;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  span {
    color: white;
    font-size: 48px;
  }
}

.wait-participant-text {
  position: absolute;
  background: white;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
}

.menu-panel {
  z-index: 5000;
  background: $dark;
  width: 100%;
  height: 45px;
  align-self: flex-end;
  display: flex;
  align-items: center;
  .q-btn {
    margin: 0 2px;
  }
}

@media (min-width: $breakpoint-md-min) {
  .video-container-card-desktop {
    right: 25px
  }
}

</style>
